import type { FC, PropsWithChildren } from "react";
import { useLocation } from "react-router-dom";
import { observer } from "mobx-react";
import { useStore } from "../stores/store";
import Navbar from "../components/navbar";
import { useEffect } from "react";
import { SidebarState } from "../stores/global.store";
import Sidebar from "src/components/sidebar";
import PaymentRequiredBanner from "../components/billing/PaymentRequiredBanner";
import { useTranslation } from "react-i18next";
import { DocsDrawerProvider } from "src/contexts/DocsDrawerContext";
import DocsDrawer from "src/components/docs/DocsDrawer";
import { WalkthroughProvider } from "src/contexts/WalkthroughContext";
import WalkthroughTour from "src/components/walkthrough/WalkthroughTour";
import { Toaster } from "src/components/ui/toast";
import { SkipLink } from "src/components/ui/skip-link";
import { ErrorBoundary } from "src/components/shared/ErrorBoundary";
import { useCommandPalette } from "src/hooks/useCommandPalette";
import { CommandPalette } from "src/components/ui/command-palette";
import { useAuth } from "src/contexts/AuthContext";

interface NavbarSidebarLayoutProps {
  isFooter?: boolean;
}

const NavbarSidebarLayout: FC<PropsWithChildren<NavbarSidebarLayoutProps>> =
  function ({ children, isFooter = true }) {
    const { globalStore } = useStore();
    const { handleResize } = globalStore;
    const { user } = useAuth();
    const commandPalette = useCommandPalette();

    useEffect(() => {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, [handleResize]);

    return (
      <DocsDrawerProvider>
        <WalkthroughProvider>
          <div className="min-h-screen bg-white">
            <SkipLink />
            <PaymentRequiredBanner />
            <Navbar onOpenCommandPalette={commandPalette.open} />
            <Sidebar />
            <ErrorBoundary>
              <MainContent isFooter={isFooter}>{children}</MainContent>
            </ErrorBoundary>
            <DocsDrawer />
            <WalkthroughTour />
            <Toaster />
            <CommandPalette
              isOpen={commandPalette.isOpen}
              onClose={commandPalette.close}
              userId={user?.id}
            />
          </div>
        </WalkthroughProvider>
      </DocsDrawerProvider>
    );
  };

const MainContent: FC<PropsWithChildren<NavbarSidebarLayoutProps>> = observer(
  function MainContent({ children, isFooter }) {
    const { globalStore } = useStore();
    const location = useLocation();
    const { i18n } = useTranslation();
    const { sidebarState, isMobileView } = globalStore;

    // Detect if current language is RTL (Arabic)
    const isRTL = i18n.language.startsWith("ar");

    const isCollapsed = sidebarState === SidebarState.COLLAPSED;
    const isIconsOnly = sidebarState === SidebarState.ICONS_ONLY;

    // On mobile, main content takes full width always
    if (isMobileView) {
      return (
        <main
          id="main-content"
          className="bg-white transition-all duration-300 ease-out flex flex-col"
          style={{
            marginTop: "0",
            height: "calc(100vh - 4rem)",
            width: "100%",
          }}
        >
          <div
            className={`flex-1 ${isFooter ? "min-h-0" : ""} overflow-y-auto w-full`}
          >
            {children}
          </div>
          {isFooter && (
            <div className="flex-shrink-0 w-full">
              <MainContentFooter />
            </div>
          )}
        </main>
      );
    }

    // Desktop styles
    const getMainMargin = () => {
      if (isCollapsed) return "";
      if (isIconsOnly) {
        return isRTL ? "mr-16" : "ml-16";
      }
      return isRTL ? "mr-64" : "ml-64";
    };

    const isStudioPage = location.pathname.includes("/workflows/studio");

    return (
      <main
        id="main-content"
        className={`bg-white transition-all duration-300 ease-out ${getMainMargin()} flex flex-col ${isStudioPage ? "overflow-hidden" : ""}`}
        style={{
          marginTop: "0",
          height: "calc(100vh - 4rem)",
          width: isCollapsed ? "100%" : isIconsOnly ? "calc(100% - 4rem)" : "calc(100% - 16rem)",
        }}
      >
        <div
          className={`flex-1 ${isFooter ? "min-h-0" : ""} ${isStudioPage ? "overflow-hidden" : "overflow-y-auto"} w-full`}
        >
          {children}
        </div>
        {isFooter && (
          <div className="flex-shrink-0 w-full">
            <MainContentFooter />
          </div>
        )}
      </main>
    );
  },
);

const MainContentFooter: FC = function () {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-slate-200 bg-white px-6 py-4 w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center">
          <img
            src="/images/botbat-logo.png"
            alt="Botbat"
            className="h-5 w-auto"
          />
          <span className="ml-2 text-sm font-medium text-slate-900">
            Botbat
          </span>
        </div>
        <div className="text-sm text-slate-500">
          © {currentYear} Botbat. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

export default NavbarSidebarLayout;
